import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Badge, Card, Empty, ErrorNote, Spinner } from "@/components/ui";
import { useToast } from "@/components/Toast";
import { api, type TaskRow } from "@/lib/api";
import { formatDue } from "@/lib/format";
import { cn } from "@/lib/utils";

function Row({ task }: { task: TaskRow }) {
  const toast = useToast();
  const qc = useQueryClient();
  const complete = useMutation({
    mutationFn: () => api.updateTask(task.id, { done: true }),
    onSuccess: () => {
      toast.push("Task completed");
      void qc.invalidateQueries();
    },
    onError: (e: Error) => toast.push(e.message, "danger"),
  });

  const overdue = task.due_at ? new Date(task.due_at) < new Date() : false;
  return (
    <tr className="border-b last:border-b-0 hover:bg-surface-2" data-testid="task-row">
      <td className="px-2 py-1.5">
        <input
          type="checkbox"
          aria-label={`Complete ${task.title}`}
          onChange={() => complete.mutate()}
          className="size-3.5"
        />
      </td>
      <td className="px-2 py-1.5 text-sm">{task.title}</td>
      <td className="px-2 py-1.5 text-xs">
        {task.entity_type === "lead" ? (
          <Link to={`/leads/${task.entity_id}`} className="hover:underline">
            {task.lead_name ?? "lead"}
          </Link>
        ) : (
          <span className="text-fg-muted">{task.entity_type}</span>
        )}
      </td>
      <td className="px-2 py-1.5">
        <Badge tone={task.priority === "p1" ? "danger" : task.priority === "p2" ? "warn" : "muted"}>
          {task.priority}
        </Badge>
      </td>
      <td className={cn("px-2 py-1.5 text-xs tabular-nums", overdue && "text-danger")}>
        {formatDue(task.due_at)}
      </td>
      <td className="px-2 py-1.5 text-xs text-fg-muted">{task.created_by}</td>
    </tr>
  );
}

/** The full open-task list Overview's "+ N more" link points at. */
export function Tasks() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["overview"],
    queryFn: () => api.overview(),
  });

  if (isLoading) return <Spinner label="Loading tasks…" />;
  if (error) return <div className="p-4"><ErrorNote error={error} /></div>;
  if (!data) return null;

  return (
    <div className="p-4">
      <header className="mb-3">
        <h1 className="text-lg font-semibold tracking-tight">Open tasks</h1>
        <p className="text-xs text-fg-muted">
          {data.open_tasks.length} open, soonest first.
        </p>
      </header>
      <Card className="overflow-hidden">
        {data.open_tasks.length === 0 ? (
          <Empty title="No open tasks." hint="Tasks created on a lead or deal show up here." />
        ) : (
          <div className="-mx-3 -my-3 overflow-x-auto">
            <table className="w-full text-sm" data-testid="tasks-table">
              <thead>
                <tr className="border-b bg-surface-2 text-left text-xs font-semibold">
                  <th className="px-2 py-1.5"> </th>
                  <th className="px-2 py-1.5">Task</th>
                  <th className="px-2 py-1.5">On</th>
                  <th className="px-2 py-1.5">Priority</th>
                  <th className="px-2 py-1.5">Due</th>
                  <th className="px-2 py-1.5">Created by</th>
                </tr>
              </thead>
              <tbody>
                {data.open_tasks.map((t) => (
                  <Row key={t.id} task={t} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
